import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useLocations, useConferirItem, useCustodians } from '../hooks';
import type { InventoryItem, Custodian } from '../api';
import { CustodianAutocomplete } from './CustodianAutocomplete';
import { Button } from '../../../shared/components/Button';
import { Alert } from '../../../shared/components/Alert';
import { Textarea } from '../../../shared/components/Textarea';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';

interface Props {
  open: boolean;
  inventoryId: string;
  item: InventoryItem | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function CheckDivergentModal({ open, inventoryId, item, onClose, onSuccess }: Props) {
  const conferir = useConferirItem(inventoryId);
  const locations = useLocations();
  const [foundCust, setFoundCust] = useState<Custodian | null>(null);
  const [foundLocId, setFoundLocId] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Fetch de custodian estava aqui só como fixup — CustodianAutocomplete é o real.
  useCustodians({}); // hydrata cache

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!item) return;
    if (!notes.trim() && !foundCust && !foundLocId) {
      setError('Aponte responsável/localização encontrados ou descreva a divergência.');
      return;
    }
    try {
      await conferir.mutateAsync({
        itemId: item.id,
        payload: {
          result: 'divergent',
          foundCustodianId: foundCust?.id,
          foundLocationId: foundLocId || undefined,
          divergenceNotes: notes.trim() || undefined,
        },
      });
      setFoundCust(null); setFoundLocId(''); setNotes(''); setError(null);
      onSuccess(); onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
      setError(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Falha ao registrar divergência'));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} className="relative z-40">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
          <Dialog.Title className="text-lg font-semibold text-orange-800">Registrar divergência</Dialog.Title>
          {item && (
            <p className="mt-1 text-xs text-gray-600">
              Ativo: <span className="font-mono">{item.asset?.displayCode ?? item.asset?.assetCode}</span> — {item.asset?.description}
            </p>
          )}
          <form onSubmit={handleSubmit} className="mt-4 space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Responsável encontrado (opcional)</label>
              <CustodianAutocomplete value={foundCust} onChange={setFoundCust} />
            </div>
            <AutocompleteSelect
              label="Localização encontrada (opcional)"
              options={[
                { value: '', label: '—' },
                ...(locations.data?.data ?? []).map((l) => ({ value: l.id, label: l.name })),
              ]}
              value={foundLocId}
              onChange={setFoundLocId}
            />
            <Textarea
              label="Observação sobre a divergência"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            {error && <Alert variant="error">{error}</Alert>}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={onClose}>Cancelar</Button>
              <Button
                type="submit"
                loading={conferir.isPending}
                className="!bg-orange-600 hover:!bg-orange-700 focus:!ring-orange-500"
              >
                {conferir.isPending ? 'Registrando…' : 'Registrar divergência'}
              </Button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
